import { Flex, Text } from "@mantine/core";
import { useFeature } from "flagged";
import {
    MantineReactTable,
    type MRT_ColumnFiltersState,
    type MRT_RowVirtualizer,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import {
    type MouseEvent,
    type UIEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { TRIP_ANALYZER } from "src/db";
import { useSearchAfterPagination } from "src/hooks/useSearchAfterPagination";
import { useUser } from "src/hooks/useUser";
import { useAdmin } from "src/utils/useAdmin";

import { flattenMetadataHits } from "./MetadataDocuments/utils";
import { tripAnalyzerColumns } from "./TripAnalyzer/columns";
import type { TripAnalyzerSchema } from "./TripAnalyzer/types";
import TripAnalyzerModal from "./TripAnalyzerModal";

const TripAnalyzer = () => {
    const columns = tripAnalyzerColumns;

    const rowVirtualizerInstanceRef =
        useRef<MRT_RowVirtualizer<HTMLDivElement, HTMLTableRowElement>>(null);

    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [selectedRow, setSelectedRow] = useState<TripAnalyzerSchema | null>(null);

    const { isLoading: isUserLoading } = useUser();
    const isAdmin = useAdmin();
    const isTripAnalyzerEnabled = useFeature("TRIP_ANALYZER");
    if (!isUserLoading && (!isAdmin || !isTripAnalyzerEnabled)) {
        throw Error("Unauthorized");
    }

    const buildPayload = useCallback(() => {
        return (
            columnFilters
                ?.filter((val) =>
                    Array.isArray(val.value)
                        ? val.value.filter(Boolean).length > 0
                        : Boolean(val.value),
                )
                .map((filter) => {
                    const key = filter.id.split("__")[1] ?? filter.id;

                    let value = filter.value;
                    if (key === "timestamp" && Array.isArray(value)) {
                        value = value.map((item) => (item == null ? "" : item));
                    }

                    return {
                        key,
                        value,
                    };
                }) ?? []
        );
    }, [columnFilters]);

    const { data, fetchNextPage, isFetching, isLoading, isError } = useSearchAfterPagination({
        queryKey: ["trip-analyzer", TRIP_ANALYZER, sorting, columnFilters],
        index: TRIP_ANALYZER,
        sorting,
        columnFilters,
        buildPayload,
    });

    const flatData = useMemo(() => flattenMetadataHits(data, isError), [data, isError]);

    // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
    const totalDBRowCount = (data as any)?.pages?.[0]?.hits?.total?.value ?? 0;

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                if (
                    scrollHeight - scrollTop - clientHeight < 400 &&
                    !isFetching &&
                    flatData.length < totalDBRowCount
                ) {
                    fetchNextPage();
                }
            }
        },
        [fetchNextPage, isFetching, flatData.length, totalDBRowCount],
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: sorting is required for correct behavior
    useEffect(() => {
        if (rowVirtualizerInstanceRef.current) {
            try {
                rowVirtualizerInstanceRef.current.scrollToIndex(0);
            } catch (e) {
                console.error(e);
            }
        }
    }, [sorting]);

    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current);
    }, [fetchMoreOnBottomReached]);

    const [rowVirtualizerRef, setRowVirtualizerRef] = useState<MRT_RowVirtualizer<
        HTMLDivElement,
        HTMLTableRowElement
    > | null>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null); //we can get access to the underlying TableContainer element and react to its scroll events

    const table = useMantineReactTable<TripAnalyzerSchema>({
        layoutMode: "grid",
        columns,
        data: flatData ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        enableColumnResizing: true,
        enableColumnFilterModes: false,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row) => {
            return `${row._id}`;
        },
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${totalDBRowCount}, (Showing ${flatData.length})`}
                </Text>
            </Flex>
        ),
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        enableGlobalFilter: false,
        enableFullScreenToggle: false,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableRowVirtualization: true,
        enableDensityToggle: false,
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: (event: MouseEvent<HTMLTableRowElement>) => {
                if ((event.target as HTMLElement).closest("a")) return;
                setSelectedRow(row.original);
            },
            sx: {
                cursor: "pointer",
            },
        }),
        mantineTableContainerProps: {
            ref: tableContainerRef, //get access to the table container element
            style: { padding: 0, borderRadius: 0, maxHeight: "80vh" },
            onScroll: (
                event: UIEvent<HTMLDivElement>, //add an event listener to the table container element
            ) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
        },
        mantinePaperProps: { style: { borderRadius: 0, zIndex: 102 } },
        onSortingChange: setSorting,
        state: { isLoading, sorting },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
        rowVirtualizerInstanceRef: ((instance) => {
            setRowVirtualizerRef(instance);
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        }) as any,
        rowVirtualizerOptions: {
            overscan: 10,
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        } as any,
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: sorting and rowVirtualizerRef are required
    useEffect(() => {
        if (rowVirtualizerRef) {
            rowVirtualizerRef.scrollToIndex(0);
        }
    }, [sorting, rowVirtualizerRef]);

    return (
        <>
            <MantineReactTable table={table} />
            <TripAnalyzerModal rowData={selectedRow} onClose={() => setSelectedRow(null)} />
        </>
    );
};

export default TripAnalyzer;
